import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  // Core Layout
  GridComponent,
  CardComponent,
  DividerComponent,
  AccordionComponent,
  TabsComponent,
  TabItemComponent,
  DialogComponent,
  // Form Inputs
  InputComponent,
  DateComponent,
  TextareaComponent,
  NumberComponent,
  PasswordComponent,
  SearchComponent,
  AutocompleteComponent,
  DropdownComponent,
  MultiSelectComponent,
  CheckboxComponent,
  RadioComponent,
  ToggleComponent,
  SliderComponent,
  TimepickerComponent,
  // Feedback
  AlertComponent,
  BadgeComponent,
  ToastService,
  ProgressBarComponent,
  ProgressSpinnerComponent,
  SkeletonComponent,
  // Buttons
  ButtonComponent,
  // Layout
  FooterComponent
} from '../ui';

@Component({
  selector: 'app-showcase-page1',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    GridComponent,
    CardComponent,
    DividerComponent,
    AccordionComponent,
    TabsComponent,
    TabItemComponent,
    DialogComponent,
    InputComponent,
    DateComponent,
    TextareaComponent,
    NumberComponent,
    PasswordComponent,
    SearchComponent,
    AutocompleteComponent,
    DropdownComponent,
    MultiSelectComponent,
    CheckboxComponent,
    RadioComponent,
    ToggleComponent,
    SliderComponent,
    TimepickerComponent,
    AlertComponent,
    BadgeComponent,
    ProgressBarComponent,
    ProgressSpinnerComponent,
    SkeletonComponent,
    ButtonComponent,
    FooterComponent
  ],
  providers: [ToastService],
  template: `
    <div class="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div class="max-w-7xl mx-auto">
        <h1 class="text-4xl font-bold text-gray-900 dark:text-white mb-2">UI Component Showcase - Page 1</h1>
        <p class="text-gray-600 dark:text-gray-400 mb-8">Core Layout, Form Inputs & Feedback Components</p>

        <!-- Buttons -->
        <app-card header="Buttons" class="mb-6">
          <div class="flex flex-wrap gap-4">
            <app-button variant="primary">Primary</app-button>
            <app-button variant="secondary">Secondary</app-button>
            <app-button variant="success">Success</app-button>
            <app-button variant="danger">Danger</app-button>
            <app-button variant="warning">Warning</app-button>
            <app-button variant="accent">Accent</app-button>
            <app-button variant="primary" [disabled]="true">Disabled</app-button>
          </div>
        </app-card>

        <!-- Grid -->
        <app-card header="Grid System" class="mb-6">
          <app-grid [columns]="12">
            <div class="col-span-12 md:col-span-6 lg:col-span-4 p-4 bg-blue-100 dark:bg-blue-900 rounded">Column 1</div>
            <div class="col-span-12 md:col-span-6 lg:col-span-4 p-4 bg-green-100 dark:bg-green-900 rounded">Column 2</div>
            <div class="col-span-12 md:col-span-12 lg:col-span-4 p-4 bg-purple-100 dark:bg-purple-900 rounded">Column 3</div>
          </app-grid>
        </app-card>

        <!-- Form Inputs -->
        <app-card header="Form Inputs" class="mb-6">
          <div class="space-y-4">
            <app-input label="Text Input" placeholder="Enter text" [(ngModel)]="formData.text" />
            <app-date label="Date Picker" [(ngModel)]="formData.date" />
            <app-textarea label="Textarea" placeholder="Enter multiline text" [(ngModel)]="formData.textarea" />
            <app-number label="Number Input" [(ngModel)]="formData.number" />
            <app-password label="Password" [(ngModel)]="formData.password" />
            <app-search label="Search" placeholder="Search..." [(ngModel)]="formData.search" />
            <app-timepicker label="Time Picker" [(ngModel)]="formData.time" />
            <app-slider label="Slider" [min]="0" [max]="100" [(ngModel)]="formData.slider" />
          </div>
        </app-card>

        <!-- Dropdowns -->
        <app-card header="Dropdowns & Selects" class="mb-6">
          <div class="space-y-4">
            <app-dropdown 
              label="Dropdown"
              [options]="dropdownOptions"
              [(ngModel)]="formData.dropdown" />
            <app-autocomplete 
              label="Autocomplete"
              [options]="autocompleteOptions"
              [(ngModel)]="formData.autocomplete" />
            <app-multi-select 
              label="Multi Select"
              [options]="multiSelectOptions"
              [(ngModel)]="formData.multiSelect" />
          </div>
        </app-card>

        <!-- Checkboxes, Radio, Toggle -->
        <app-card header="Checkboxes, Radio & Toggle" class="mb-6">
          <div class="space-y-4">
            <app-checkbox label="Checkbox Option 1" [(ngModel)]="formData.checkbox1" />
            <app-checkbox label="Checkbox Option 2" [(ngModel)]="formData.checkbox2" />
            <app-radio 
              label="Radio Group"
              [options]="radioOptions"
              [(ngModel)]="formData.radio" />
            <app-toggle label="Toggle Switch" [(ngModel)]="formData.toggle" />
          </div>
        </app-card>

        <!-- Alerts -->
        <app-card header="Alerts" class="mb-6">
          <div class="space-y-4">
            <app-alert variant="success" message="Success alert message" />
            <app-alert variant="danger" message="Error alert message" />
            <app-alert variant="warning" message="Warning alert message" />
            <app-alert variant="info" message="Info alert message" />
          </div>
        </app-card>

        <!-- Badges -->
        <app-card header="Badges" class="mb-6">
          <div class="flex flex-wrap gap-2">
            <app-badge variant="primary" text="Primary" />
            <app-badge variant="success" text="Success" />
            <app-badge variant="danger" text="Danger" />
            <app-badge variant="warning" text="Warning" />
            <app-badge variant="info" text="Info" />
          </div>
        </app-card>

        <!-- Progress -->
        <app-card header="Progress Indicators" class="mb-6">
          <div class="space-y-4">
            <app-progress-bar [value]="progressValue()" label="Progress Bar" [showValue]="true" />
            <app-progress-spinner variant="primary" size="md" label="Loading..." />
          </div>
        </app-card>

        <!-- Tabs -->
        <app-card header="Tabs" class="mb-6">
          <app-tabs>
            <app-tab-item id="tab1" label="Tab 1">
              <p class="p-4">Content for Tab 1</p>
            </app-tab-item>
            <app-tab-item id="tab2" label="Tab 2">
              <p class="p-4">Content for Tab 2</p>
            </app-tab-item>
            <app-tab-item id="tab3" label="Tab 3">
              <p class="p-4">Content for Tab 3</p>
            </app-tab-item>
          </app-tabs>
        </app-card>

        <!-- Accordion -->
        <app-card header="Accordion" class="mb-6">
          <app-accordion [items]="accordionItems" />
        </app-card>

        <!-- Dialog -->
        <app-card header="Dialog" class="mb-6">
          <app-button variant="primary" (clicked)="showDialog.set(true)">Open Dialog</app-button>
          <app-dialog [visible]="showDialog()" header="Sample Dialog" (visibleChange)="showDialog.set($event)">
            <p>This is a dialog content. You can add any content here.</p>
          </app-dialog>
        </app-card>

        <!-- Toast Buttons -->
        <app-card header="Toast Notifications" class="mb-6">
          <div class="flex flex-wrap gap-2">
            <app-button variant="success" (clicked)="showToast('success', 'Success message!')">Success Toast</app-button>
            <app-button variant="danger" (clicked)="showToast('error', 'Error message!')">Error Toast</app-button>
            <app-button variant="warning" (clicked)="showToast('warning', 'Warning message!')">Warning Toast</app-button>
            <app-button variant="accent" (clicked)="showToast('info', 'Info message!')">Info Toast</app-button>
          </div>
        </app-card>

        <!-- Skeleton -->
        <app-card header="Skeleton Loader" class="mb-6">
          <div class="space-y-2">
            <app-skeleton [width]="'100%'" [height]="'20px'" />
            <app-skeleton [width]="'100%'" [height]="'20px'" />
            <app-skeleton [width]="'100%'" [height]="'20px'" />
          </div>
        </app-card>

        <!-- Divider -->
        <app-divider />

        <!-- Footer -->
        <div class="mt-8">
          <app-footer copyright="© 2024 UI Component Library. All rights reserved." />
        </div>
      </div>
    </div>
  `,
  styles: []
})
export class ShowcasePage1Component {
  constructor(private toastService: ToastService) {}

  showDialog = signal(false);
  progressValue = signal(45);

  formData = {
    text: '',
    date: '',
    textarea: '',
    number: 0,
    password: '',
    search: '',
    time: '',
    slider: 50,
    dropdown: null as any,
    autocomplete: null as any,
    multiSelect: [] as any[],
    checkbox1: false,
    checkbox2: false,
    radio: null as any,
    toggle: false
  };

  dropdownOptions = [
    { label: 'Option 1', value: 'opt1' },
    { label: 'Option 2', value: 'opt2' },
    { label: 'Option 3', value: 'opt3' }
  ];

  autocompleteOptions = [
    { label: 'Apple', value: 'apple' },
    { label: 'Banana', value: 'banana' },
    { label: 'Cherry', value: 'cherry' },
    { label: 'Date', value: 'date' }
  ];

  multiSelectOptions = [
    { label: 'Red', value: 'red' },
    { label: 'Green', value: 'green' },
    { label: 'Blue', value: 'blue' },
    { label: 'Yellow', value: 'yellow' }
  ];

  radioOptions = [
    { label: 'Option A', value: 'a' },
    { label: 'Option B', value: 'b' },
    { label: 'Option C', value: 'c' }
  ];

  accordionItems = [
    { header: 'Section 1', content: 'Content for section 1' },
    { header: 'Section 2', content: 'Content for section 2' },
    { header: 'Section 3', content: 'Content for section 3' }
  ];

  showToast(type: 'success' | 'error' | 'warning' | 'info', message: string): void {
    const variantMap = {
      success: 'success' as const,
      error: 'danger' as const,
      warning: 'warning' as const,
      info: 'info' as const
    };
    this.toastService.show({
      variant: variantMap[type],
      detail: message,
      life: 3000
    });
  }
}
